'use client';

/**
 * Facebook (Meta) App Events integration (native-only).
 *
 * Same architecture note as src/lib/appsflyer.ts: this app is a Next.js
 * site loaded *remotely* into a Capacitor WebView. The Facebook SDK runs
 * natively on the device; this module talks to it through the
 * `@capacitor-community/facebook-login` bridge (it exposes App Events
 * logging alongside login — we only use the event-logging half here). On
 * the plain web build every call is a no-op, so it is always safe to import.
 *
 * Why this exists: Meta currently has no visibility into what a user does
 * in the app/site after clicking a Facebook ad (which service they picked,
 * whether they purchased) — the SDK reports that back so ad campaigns can
 * be measured and audiences built from real conversions.
 *
 * This wires up initialization + a generic event logger. Actually calling
 * `logFacebookEvent(...)` at each meaningful step (service selected,
 * request created, payment completed, ...) is deliberately left for
 * follow-up work, one funnel step at a time.
 */

import { Capacitor } from '@capacitor/core';

// Public by design — ships in every mobile binary, same class of value as
// the AppsFlyer dev key in appsflyer.ts. NEXT_PUBLIC_* so it's available in
// the WebView bundle.
const APP_ID = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '';
const CLIENT_TOKEN = process.env.NEXT_PUBLIC_FACEBOOK_CLIENT_TOKEN || '';

let initialized = false;

/**
 * iOS 14.5+ requires explicit user consent (App Tracking Transparency)
 * before the IDFA can be used. Without it Meta only ever gets aggregated
 * SKAdNetwork data — no user-level ad→conversion matching — so we ask, then
 * tell the SDK what the user chose. iOS-only: the plugin has no Android
 * implementation, and Android has no equivalent prompt.
 *
 * Deliberately called *before* the first events are logged, since the
 * setting applies to everything the SDK sends afterwards.
 */
async function syncAdvertiserTracking(): Promise<void> {
  if (Capacitor.getPlatform() !== 'ios') return;
  try {
    const { AppTrackingTransparency } = await import(
      'capacitor-plugin-app-tracking-transparency'
    );
    const { status } = await AppTrackingTransparency.requestPermission();
    const authorized = status === 'authorized';
    const { FacebookLogin } = await import('@capacitor-community/facebook-login');
    await FacebookLogin.setAdvertiserTrackingEnabled({ enabled: authorized });
    await FacebookLogin.setAdvertiserIDCollectionEnabled({ enabled: authorized });
  } catch (err) {
    console.warn('[facebook] ATT permission flow failed', err);
  }
}

/**
 * Initializes the Facebook SDK exactly once. No-op on web (native config is
 * baked into Info.plist / AndroidManifest.xml instead, per Meta's own
 * setup) and on repeat calls. Safe to await from app startup.
 */
export async function initFacebookSdk(): Promise<void> {
  if (initialized) return;
  if (!Capacitor.isNativePlatform()) return;
  if (!APP_ID || !CLIENT_TOKEN) {
    console.warn('[facebook] no App ID / Client Token configured — skipping init');
    return;
  }
  initialized = true;
  try {
    const { FacebookLogin } = await import('@capacitor-community/facebook-login');
    // initialize() is a documented no-op on Android/iOS (config there comes
    // from native files), but calling it uniformly keeps this module's
    // public API platform-agnostic and covers the web fallback.
    await FacebookLogin.initialize({ appId: APP_ID });
    // The install/open events Meta attributes back to an ad click
    // (fb_mobile_first_app_launch, fb_mobile_activate_app) are the whole
    // point of this integration — "did the person who clicked the ad
    // actually install the app". They ride on auto-logging, so ask for it
    // explicitly rather than relying on the SDK default staying true.
    await FacebookLogin.setAutoLogAppEventsEnabled({ enabled: true });
    await syncAdvertiserTracking();
  } catch (err) {
    console.warn('[facebook] init failed', err);
  }
}

/**
 * Logs a Facebook App Event (for ad attribution / conversion analytics).
 * No-op on web or before initFacebookSdk() has run. `parameters` values
 * must be strings or numbers per the native SDK's constraints.
 */
export async function logFacebookEvent(
  eventName: string,
  parameters: Record<string, string | number> = {}
): Promise<void> {
  if (!initialized || !Capacitor.isNativePlatform()) return;
  try {
    const { FacebookLogin } = await import('@capacitor-community/facebook-login');
    await FacebookLogin.logEvent({ eventName, parameters });
  } catch (err) {
    console.warn('[facebook] logEvent failed', err);
  }
}
